import { Box, Card, CardContent, CardHeader, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Close, CloudUpload } from '@material-ui/icons';
import { Button } from '@roc/ui';
import { observer } from 'mobx-react';
import { FC, SyntheticEvent, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useStore } from '../../../hooks';

const MAX_ID_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_ID_FILE_SIZE_MESSAGE = 'File must be an image or PDF no larger than 20 MB.';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: 900,
    margin: 'auto',
    padding: theme.spacing(4),
  },
  card: {
    marginTop: theme.spacing(2),
  },
  dragArea: {
    border: '2px dotted #427ED1',
    width: '100%',
    backgroundColor: '#EAF4FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: 20,
    borderRadius: 5,
    cursor: 'pointer',
  },
  icon: {
    width: '3vw',
    height: '3vw',
    minWidth: 24,
    minHeight: 24,
    color: '#4B89DA',
  },
  fileLine: {
    display: 'flex',
    alignItems: 'center',
  },
  removeButton: {
    marginLeft: theme.spacing(2),
  },
}));

export const IdDocumentUploadStep = observer(() => {
  const classes = useStyles();
  const { wireAuthorizationStore } = useStore();

  return (
    <Box className={classes.container}>
      <Typography variant="h5" gutterBottom>
        Upload Identification
      </Typography>
      <Typography variant="body1" gutterBottom>
        Please upload a photo of the front and back of a valid, government-issued photo ID (driver's license or passport).
      </Typography>
      <IdDocumentSideUpload
        title="Identification Document - Front*"
        testId="identityDocumentFront"
        file={wireAuthorizationStore.identityDocumentFront}
        onChange={file => wireAuthorizationStore.setIdentityDocumentFront(file)}
      />
      <IdDocumentSideUpload
        title="Identification Document - Back*"
        testId="identityDocumentBack"
        file={wireAuthorizationStore.identityDocumentBack}
        onChange={file => wireAuthorizationStore.setIdentityDocumentBack(file)}
      />
    </Box>
  );
});

interface IdDocumentSideUploadProps {
  title: string;
  testId: string;
  file: File | null;
  onChange: (file: File | null) => void;
}

const IdDocumentSideUpload: FC<IdDocumentSideUploadProps> = ({ title, testId, file, onChange }) => {
  const classes = useStyles();
  const [error, setError] = useState<string | null>(null);

  const onDrop = (newFiles: File[]) => {
    setError(null);
    onChange(newFiles?.[0] ?? null);
  };

  const onDropRejected = () => {
    setError(MAX_ID_FILE_SIZE_MESSAGE);
    onChange(null);
  };

  const handleRemove = (event: SyntheticEvent) => {
    event.stopPropagation();
    onChange(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: false,
    maxSize: MAX_ID_FILE_SIZE_BYTES,
    accept: ['image/*', 'application/pdf'],
  });

  return (
    <Card variant="outlined" className={classes.card}>
      <CardHeader title={title} />
      <CardContent>
        <Box {...getRootProps()} className={classes.dragArea}>
          <input {...getInputProps()} />
          <CloudUpload className={classes.icon} />
          {file ? (
            <Box className={classes.fileLine}>
              <Typography>{file.name}</Typography>
              <Button
                testId={`remove-${testId}`}
                size="small"
                variant="outlined"
                className={classes.removeButton}
                onClick={handleRemove}
              >
                REMOVE <Close />
              </Button>
            </Box>
          ) : isDragActive ? (
            <p>Drop the file here...</p>
          ) : (
            <>
              <p>Drag file to upload</p>
              <p>or Browse file</p>
            </>
          )}
        </Box>
        {error && (
          <Typography color="error" variant="body2" gutterBottom>
            {error}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
